import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangleIcon, BellIcon, CheckCircle2Icon, FileTextIcon, MessageSquareIcon, SparklesIcon, UserPlusIcon } from 'lucide-react';
import { notifications as seedNotifications, type Notification, type NotificationKind } from '../data/notifications';
const iconByKind: Record<NotificationKind, React.ElementType> = {
  'new-lead': UserPlusIcon,
  reply: MessageSquareIcon,
  'app-submitted': FileTextIcon,
  'ai-review': SparklesIcon,
  overdue: AlertTriangleIcon,
  'clear-to-close': CheckCircle2Icon
};
const iconTone: Record<NotificationKind, string> = {
  'new-lead': 'bg-brand-soft text-brand-dark',
  reply: 'bg-navy-soft text-navy',
  'app-submitted': 'bg-gray-100 text-gray-600',
  'ai-review': 'bg-brand-soft text-brand-dark',
  overdue: 'bg-danger-soft text-danger',
  'clear-to-close': 'bg-success-soft text-success'
};
export function NotificationMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>(seedNotifications);
  const containerRef = useRef<HTMLDivElement>(null);
  const unreadCount = items.filter(item => item.unread).length;
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const markAllRead = () => setItems(prev => prev.map(item => ({
    ...item,
    unread: false
  })));
  const openItem = (notification: Notification) => {
    setItems(prev => prev.map(item => item.id === notification.id ? {
      ...item,
      unread: false
    } : item));
    setOpen(false);
    navigate(notification.to);
  };
  return <div className="relative" ref={containerRef} data-id="element-524" data-mp="21d840y0jac93">
      <button type="button" onClick={() => setOpen(prev => !prev)} aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'} aria-expanded={open} aria-haspopup="true" className="relative rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand/30" data-id="element-525" data-mp="21bn5yyf0gb6h">
        <BellIcon className="h-4 w-4" aria-hidden="true" data-id="element-526" data-mp="1dnnj49ktl4x6" />
        {unreadCount > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[9.5px] font-bold text-white" data-id="element-527" data-mp="1dlsxet1q58s1">
            {unreadCount}
          </span>}
      </button>

      <AnimatePresence data-id="element-528" data-mp="3gau62zsh60kf">
        {open && <motion.div role="menu" aria-label="Notifications" initial={{
        opacity: 0,
        y: -6,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -6,
        scale: 0.98
      }} transition={{
        duration: 0.14
      }} className="absolute right-0 z-50 mt-2 w-[360px] origin-top-right overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl" data-id="element-529" data-mp="3ga18aobhulef">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3" data-id="element-530" data-mp="31kwkmrlxjoyx">
              <h2 className="text-[13.5px] font-semibold text-ink" data-id="element-531" data-mp="36krb4u1smcjg">Notifications</h2>
              {unreadCount > 0 && <span className="rounded-full bg-danger-soft px-2 py-0.5 text-[10.5px] font-semibold text-danger" data-id="element-532" data-mp="3bc9etpv7ar84">
                  {unreadCount} new
                </span>}
              <button type="button" onClick={markAllRead} disabled={unreadCount === 0} className="ml-auto text-[12px] font-medium text-brand transition-colors hover:text-brand-dark disabled:text-gray-400" data-id="element-533" data-mp="2iu57deytnef9">
                Mark all read
              </button>
            </div>

            <ul className="scrollbar-thin max-h-[380px] divide-y divide-gray-100 overflow-y-auto" data-id="element-534" data-mp="y6cipyel8k6w">
              {items.map(item => {
            const Icon = iconByKind[item.kind];
            return <li key={item.id} data-id="element-535" data-mp="133kzpvo02i6k">
                    <button type="button" role="menuitem" onClick={() => openItem(item)} className={['flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50', item.unread ? 'bg-brand-soft/40' : 'bg-white'].join(' ')} data-id="element-536" data-mp="132jh964nmtet">
                      <span className={['mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md', iconTone[item.kind]].join(' ')} data-id="element-537" data-mp="3euq4fvx7l2mn">
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-538" data-mp="2v06ut04sa3jg" />
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-539" data-mp="2zxgk8fs6af7a">
                        <span className="block text-[12.5px] font-semibold leading-snug text-ink" data-id="element-540" data-mp="2l17nbkvr8dy1">
                          {item.title}
                        </span>
                        <span className="mt-0.5 block text-[12px] leading-snug text-gray-600" data-id="element-541" data-mp="2q1lxb8gx1xyp">
                          {item.body}
                        </span>
                        <span className="mt-1 block text-[11px] text-gray-400" data-id="element-542" data-mp="2gyukb48e6u4y">
                          {item.time}
                        </span>
                      </span>
                      {item.unread && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-label="Unread" data-id="element-543" data-mp="f6yabz1vhsca" />}
                    </button>
                  </li>;
          })}
            </ul>

            {items.length === 0 && <p className="px-4 py-8 text-center text-[12.5px] text-gray-500" data-id="element-544" data-mp="3uz7gtqitba6w">
                You're all caught up.
              </p>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}